import React, { useState } from 'react';

const AddMemberModal = ({ isOpen, onClose, onMemberAdded, loading }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    areaCode: '+1',
    phone: ''
  });
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  // Email validation helper
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation helper
  const validatePhone = (phone) => {
    const phoneDigits = phone.replace(/\D/g, '');
    return phoneDigits.length >= 10 && phoneDigits.length <= 15;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    const newErrors = { ...errors };
    
    if (name === 'email' && value.trim() && !validateEmail(value)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (name === 'phone' && value.trim() && !validatePhone(value)) {
      newErrors.phone = 'Please enter a valid phone number (10-15 digits)';
    }
    
    setErrors(newErrors);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // First name is required
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    // Last name is required
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    // Phone number is required
    const hasPhone = formData.phone.trim();
    
    if (!hasPhone) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      // Validate phone format if provided
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    // Validate email format if provided
    const hasEmail = formData.email.trim();
    if (hasEmail && !validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    onMemberAdded(formData);
  };

  const handleClose = () => {
    setFormData({ firstName: '', lastName: '', email: '', areaCode: '+1', phone: '' });
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Add a Member</h2>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="flex gap-4 mb-5">
              <div className="flex-1">
                <label htmlFor="firstName" className="block mb-1.5 font-medium text-gray-700">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2.5 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed`}
                  placeholder="First name"
                  disabled={loading}
                />
                {errors.firstName && <span className="text-red-600 text-xs mt-1 block">{errors.firstName}</span>}
              </div>

              <div className="flex-1">
                <label htmlFor="lastName" className="block mb-1.5 font-medium text-gray-700">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2.5 border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed`}
                  placeholder="Last name"
                  disabled={loading}
                />
                {errors.lastName && <span className="text-red-600 text-xs mt-1 block">{errors.lastName}</span>}
              </div>
            </div>

            <div className="mb-5">
              <label htmlFor="email" className="block mb-1.5 font-medium text-gray-700">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className={`w-full px-3 py-2.5 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed`}
                placeholder="Enter email address"
                disabled={loading}
              />
              {errors.email && <span className="text-red-600 text-xs mt-1 block">{errors.email}</span>}
            </div>

            <div className="mb-5">
              <label htmlFor="phone" className="block mb-1.5 font-medium text-gray-700">Phone Number *</label>
              <div className="flex gap-2.5 w-full">
                <select
                  name="areaCode"
                  value={formData.areaCode}
                  onChange={handleInputChange}
                  className="px-2 py-2.5 text-sm w-24 min-w-24 flex-shrink-0 cursor-pointer border-b-2 border-gray-300 bg-transparent focus:outline-none focus:border-blue-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+39">🇮🇹 +39</option>
                  <option value="+34">🇪🇸 +34</option>
                  <option value="+31">🇳🇱 +31</option>
                  <option value="+32">🇧🇪 +32</option>
                  <option value="+41">🇨🇭 +41</option>
                  <option value="+43">🇦🇹 +43</option>
                  <option value="+45">🇩🇰 +45</option>
                  <option value="+46">🇸🇪 +46</option>
                  <option value="+47">🇳🇴 +47</option>
                  <option value="+358">🇫🇮 +358</option>
                  <option value="+972">🇮🇱 +972</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+64">🇳🇿 +64</option>
                  <option value="+81">🇯🇵 +81</option>
                  <option value="+82">🇰🇷 +82</option>
                  <option value="+86">🇨🇳 +86</option>
                  <option value="+91">🇮🇳 +91</option>
                </select>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  className={`flex-1 min-w-0 px-2 py-2.5 text-sm border-b-2 ${errors.phone ? 'border-red-500' : 'border-gray-300'} bg-transparent focus:outline-none focus:border-blue-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed`}
                  placeholder="Enter phone number"
                  disabled={loading}
                />
              </div>
              {errors.phone && <span className="text-red-600 text-xs mt-1 block">{errors.phone}</span>}
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-gray-700">
              <p>The member will appear in your community when they sign into the app with this phone or email.</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button 
              type="button" 
              className="px-6 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 hover:border-gray-400 transition-all hover:-translate-y-px disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all hover:-translate-y-px hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMemberModal;